import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetActiveOrganization } from '@repo/api-client/react';
import { useCreateOrganization, useUploadOrgLogo } from '@repo/queries';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { BusinessForm } from '@/features/org/BusinessForm';
import { businessFormToCreate, type BusinessFormValues } from '@/features/org/businessFormSchema';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';
import { BusinessAvatar, StagedImagePicker } from '@/features/avatar';

export function BusinessCreateScreen() {
  const navigate = useNavigate();
  const { createOrganization, loading } = useCreateOrganization();
  const { uploadLogo } = useUploadOrgLogo();
  const setActive = useSetActiveOrganization();
  const { toast } = useToast();

  // Logo lives outside the form schema — there's no presigned-URL
  // path, the endpoint is `PUT /orgs/:id/logo` and we don't have an
  // id until createOrganization resolves. Stage the file here and
  // fire the multipart upload after the create.
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [stagedName, setStagedName] = useState<string>('');

  async function handleSubmit(values: BusinessFormValues) {
    try {
      const org = await createOrganization(businessFormToCreate(values));
      setActive(org.id);
      // Fire-and-forget the logo upload — the org already exists, so
      // navigation can proceed regardless. A failure surfaces via
      // toast; we don't block the redirect on it.
      if (logoFile) {
        try {
          await uploadLogo({ organizationId: org.id, file: logoFile });
        } catch (err) {
          const desc = describeAppError(err);
          toast({
            title: 'Logo upload failed',
            description: desc.message,
            variant: 'danger',
          });
        }
      }
      toast({ title: 'Business created', variant: 'success' });
      navigate(`/settings/businesses/${org.id}`);
    } catch (err) {
      const desc = describeAppError(err);
      toast({ title: desc.title, description: desc.message, variant: 'danger' });
    }
  }

  return (
    <Page>
      <PageHeader
        title="New business"
        description="Each business gets its own clients, invoices, services and tax settings."
      />
      <BusinessForm
        loading={loading}
        submitLabel="Create business"
        onCancel={() => navigate('/settings/businesses')}
        onSubmit={(values) => {
          // Snapshot the name so the preview tile shows the right
          // initials/colour while the create request is in flight.
          setStagedName(values.name ?? '');
          return handleSubmit(values);
        }}
        logoSlot={
          <StagedImagePicker
            title="Logo"
            hint="Square images render best. PNG, JPG or WebP up to 5 MB."
            file={logoFile}
            onChange={setLogoFile}
            renderPreview={(previewUrl) => (
              <BusinessAvatar
                id="pending"
                name={stagedName || 'New business'}
                logoUrl={previewUrl}
                size={64}
              />
            )}
          />
        }
      />
    </Page>
  );
}
